import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import FontBody1 from '../../../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../../components/fonts/font-props';
import IconDelete from '../../../../../../components/icons/icon-delete/icon-delete';
import IconPlus from '../../../../../../components/icons/icon-plus/icon-plus';
import Icon from '../../../../../../components/icons/icon/icon';
import { ButtonIcon } from '../../../../../../components/inputs/button-icon/button-icon';
import { BUTTON_VARIANTS } from '../../../../../../components/inputs/button-variants';
import { Button } from '../../../../../../components/inputs/button/button';
import ControlGroup from '../../../../../../components/layouts/control-group/control-group';
import useTranslation from '../../../../../../translation/hooks/translation-hook';
import Translation from '../../../../../../translation/translation';
import TranslationInput from '../../../../../../translation/translation-input/translation-input';
import { FOLLOW_UP_QUESTION_TYPE, FOLLOW_UP_QUESTION_TYPE_MAP } from '../../../../constants';
import FollowUpQuestionAnswerStore from '../../../../stores/follow-up-question-answer-store';
import { IQuestionTypeProps } from '../question-types-props';
import './multi-choice.scss';

import { List } from 'react-movable';
import IconSort from '../../../../../../components/icons/icon-sort/icon-sort';
import { IValidationGroup } from '../../../../../../hooks/validation-group/validation-group-hook';
import FollowUpQuestionStore from '../../../../stores/follow-up-question-store';

interface IAnswerProps {
    type: FOLLOW_UP_QUESTION_TYPE; 
    answer: FollowUpQuestionAnswerStore;
    disabled: boolean;
    disableDelete: boolean;
    validationGroup?: IValidationGroup;
}

const Answer: React.FC<IAnswerProps> = observer(({ type, answer, disabled, disableDelete, validationGroup}) => {
    const isYesNo = type === FOLLOW_UP_QUESTION_TYPE.YesNo;

    const { setDeletedKeys } = useTranslation();

    const renderIcon = (isYesNo:boolean) => {
        const typeKey = isYesNo ? FOLLOW_UP_QUESTION_TYPE.MultiChoiceSingleAnswer : type;
        const iconComponent = FOLLOW_UP_QUESTION_TYPE_MAP[typeKey].icon;
        if (!iconComponent) {
            return null;
        }
        return <Icon Component={iconComponent} />;
    };
    
    const renderField = (translationKey:string) => {
        return (
            disabled 
                ? <Translation Font={FontBody1} color={FONT_COLOR.PRIMARY3} translationKey={translationKey} />
                : <TranslationInput translationKey={translationKey} required validationGroup={validationGroup} />
        );
    };

    return (
        <div className={`answer-multi-choice-type ${disabled && 'disabled'}`} >
            {!disabled && <div data-movable-handle="true" className="item-move">
                <Icon Component={IconSort} />
            </div>}
            {renderIcon(isYesNo)}
            {renderField(answer.displayName)}
            {answer.deleted}
            {!disabled && <ButtonIcon 
                Component={IconDelete} 
                disabled={disableDelete}
                onClick={() => {
                    answer.setDeleted(setDeletedKeys);    
                }}
            />}
        </div>
    );
});

const FuqMultiChoice: React.FC<IQuestionTypeProps> = observer(({
    // DICLAIMER: before migration to React 18, it was reusing `answerDefaultProps` from question-types-props.ts
    // TODO: discuss during CR
    question = {} as FollowUpQuestionStore,
    disabled = false,
    validationGroup
}) => {
    const minAnswers = 2;
    const list = question?.answers.filter(answer => !answer.deleted) ?? [];
    const ref = useRef({} as HTMLDivElement);

    const focusAnswer = () => {
        setTimeout(() => {
            if (ref?.current) {
                ref.current.querySelector<HTMLInputElement>('.answer-multi-choice > div:last-child input')?.focus();
            }
        });            
    };

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        /** if there was a try to reorder, but cancelled */
        if (oldIndex === newIndex) {
            return;
        }

        list?.forEach((answer, index) => {
            var down = oldIndex > newIndex;
            var changedIndex = down ? (index + 1) : (index - 1);
            var range = down ? (index <= oldIndex && index >= newIndex) : (index >= oldIndex && index <= newIndex);
            let newWeight = index;

            if (range) {
                newWeight = (index === oldIndex) ? newIndex : changedIndex;
            }
            answer.setWeight(newWeight);         
        });
    };

    const renderAnswers = () => (
        <List
            values={list}
            onChange={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex)}
            renderList={({ children, props }) => <div className="answer-multi-choice" {...props}>{children}</div>}
            renderItem={({ value, props, isDragged }) => 
                <div className={`is-dragged-${isDragged}`} {...props}>
                    <Answer
                        disabled={disabled ?? false}
                        key={value.id}
                        type={question!.type}
                        answer={value}
                        validationGroup={validationGroup}
                        disableDelete={question ? question.answers.filter((item) => !item.deleted).length <= minAnswers : false}
                    />
                </div>
            }
        />
    );
    
    return (
        <div className="multi-choice" ref={ref}>
            {disabled && <div className="answer-title">
                <Translation Font={FontBody1} variant={FONT_VARIANT.BOLD} translationKey={question?.question as string} />
                <Translation Font={FontBody1} translationKey={question?.instruction as string} />
            </div>}
            {!disabled && <ControlGroup>{renderAnswers()}</ControlGroup>}
            {disabled && renderAnswers()}

            {(!disabled && question?.type !== FOLLOW_UP_QUESTION_TYPE.YesNo) &&
                <div className="add-answer-button">
                    <Button
                        variant={BUTTON_VARIANTS.Text}
                        text="add answer"
                        leftIcon={IconPlus}
                        onClick={() => {
                            question?.addAnswer();
                            focusAnswer();
                        }}
                    />
                </div>
            }
        </div>
    );
});

export default FuqMultiChoice;
